import { FC, MouseEvent, useState } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Popover,
  SxProps,
  Theme,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  DECATHLON_DARK_GREY_COLOR,
  SHARE_LOGO_BLUE_COLOR,
  SHARE_LOGO_ORANGE_COLOR
} from '../constants/colors';
import { getDecodedJWT, getToken } from '../auth/session';
import MyTheme from '../theme/MyTheme';
import { changeLanguage } from '../i18n/i18n';
import { enumLanguage } from '../types/enumeration';
import FlagGb from '../images/flags/gb.svg';
import FlagFr from '../images/flags/fr.svg';

// Each language is named in itself, so that it can be found by someone who does not read the current one
const LANGUAGES: Array<{ code: enumLanguage; name: string; flag: string }> = [
  { code: enumLanguage.en, name: 'English', flag: FlagGb },
  { code: enumLanguage.fr, name: 'Français', flag: FlagFr }
];

const languageChoiceStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: MyTheme.spacing(0.75),
  padding: MyTheme.spacing(0.5, 1),
  borderRadius: 4,
  fontSize: 13,
  fontWeight: 500,
  color: DECATHLON_DARK_GREY_COLOR,
  transition: 'background-color 150ms ease-in-out, color 150ms ease-in-out',
  '& img': {
    opacity: 0.5,
    transition: 'opacity 150ms ease-in-out'
  },
  '&:hover': {
    color: SHARE_LOGO_BLUE_COLOR,
    '& img': { opacity: 1 }
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${SHARE_LOGO_BLUE_COLOR}`,
    outlineOffset: 1
  }
};

type JwtUser = {
  id?: string;
  displayName?: string;
  other?: { mail?: string };
};

const avatarStyle = {
  fontWeight: 700,
  letterSpacing: 0.5,
  bgcolor: SHARE_LOGO_BLUE_COLOR,
  color: SHARE_LOGO_ORANGE_COLOR
};

const ellipsisStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

const sxStyles: Record<string, SxProps<Theme>> = {
  trigger: {
    marginLeft: MyTheme.spacing(1),
    borderRadius: '50%',
    '&.Mui-focusVisible': {
      outline: `2px solid ${SHARE_LOGO_BLUE_COLOR}`,
      outlineOffset: 2
    }
  },
  avatar: {
    ...avatarStyle,
    width: 36,
    height: 36,
    fontSize: 15
  },
  largeAvatar: {
    ...avatarStyle,
    width: 56,
    height: 56,
    fontSize: 22
  },
  card: {
    width: 300,
    padding: MyTheme.spacing(2)
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: MyTheme.spacing(2),
    marginBottom: MyTheme.spacing(2)
  },
  identityText: {
    minWidth: 0
  },
  name: {
    ...ellipsisStyle,
    fontWeight: 600,
    color: SHARE_LOGO_BLUE_COLOR
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: MyTheme.spacing(2),
    marginTop: MyTheme.spacing(1.5)
  },
  mail: {
    ...ellipsisStyle,
    color: DECATHLON_DARK_GREY_COLOR
  },
  rowLabel: {
    color: DECATHLON_DARK_GREY_COLOR
  },
  rowValue: {
    fontWeight: 500,
    textAlign: 'right'
  },
  languageRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: MyTheme.spacing(1)
  },
  languageChoices: {
    display: 'flex',
    gap: MyTheme.spacing(0.5)
  },
  languageChoice: languageChoiceStyle,
  languageChoiceSelected: {
    ...languageChoiceStyle,
    fontWeight: 600,
    color: SHARE_LOGO_BLUE_COLOR,
    bgcolor: alpha(SHARE_LOGO_BLUE_COLOR, 0.08),
    '& img': { opacity: 1 }
  },
  flag: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.12)'
  }
};

export const getInitials = (displayName: string | undefined): string => {
  let result = '';

  if (displayName) {
    const words = displayName.trim().split(/\s+/).filter(Boolean);
    if (words.length > 1) {
      result = `${words[0][0]}${words[words.length - 1][0]}`;
    } else if (words.length === 1) {
      result = words[0].slice(0, 2);
    }
  }

  return result.toUpperCase();
};

const UserMenu: FC = () => {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const decodedJwt = getDecodedJWT(getToken());
  const user = (decodedJwt?.user || {}) as JwtUser;
  const displayName = user.displayName ?? user.id ?? '';
  const initials = getInitials(displayName);
  const mail = user.other?.mail;
  // iat is renewed on each refresh, firstIssuedAt keeps the login time once the token has been refreshed
  const sessionStart =
    (decodedJwt?.firstIssuedAt as number | undefined) ?? decodedJwt?.iat;
  const startedAt = sessionStart
    ? new Date(sessionStart * 1000).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    : null;

  const open = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const close = () => {
    setAnchorEl(null);
  };
  const onChangeLanguage = (language: string) => {
    if (language !== i18n.resolvedLanguage) {
      changeLanguage(language);
    }
  };

  let result = null;

  if (decodedJwt) {
    result = (
      <>
        <ButtonBase
          disableRipple
          sx={sxStyles.trigger}
          onClick={open}
          aria-label={t('decathlon_user_menu_label') as string}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
        >
          <Avatar sx={sxStyles.avatar}>{initials}</Avatar>
        </ButtonBase>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { marginTop: MyTheme.spacing(1) } }}
        >
          <Box sx={sxStyles.card}>
            <Box sx={sxStyles.identity}>
              <Avatar sx={sxStyles.largeAvatar}>{initials}</Avatar>
              <Box sx={sxStyles.identityText}>
                <Typography sx={sxStyles.name}>{displayName}</Typography>
                {mail && (
                  <Typography variant="body2" sx={sxStyles.mail} title={mail}>
                    {mail}
                  </Typography>
                )}
              </Box>
            </Box>
            <Divider />
            {user.id && (
              <Box sx={sxStyles.row}>
                <Typography variant="body2" sx={sxStyles.rowLabel}>
                  {t('decathlon_user_id_label')}
                </Typography>
                <Typography variant="body2" sx={sxStyles.rowValue}>
                  {user.id}
                </Typography>
              </Box>
            )}
            {startedAt && (
              <Box sx={sxStyles.row}>
                <Typography variant="body2" sx={sxStyles.rowLabel}>
                  {t('decathlon_user_session_started_label')}
                </Typography>
                <Typography variant="body2" sx={sxStyles.rowValue}>
                  {startedAt}
                </Typography>
              </Box>
            )}
            <Box sx={sxStyles.languageRow}>
              <Typography variant="body2" sx={sxStyles.rowLabel}>
                {t('decathlon_user_language_label')}
              </Typography>
              <Box sx={sxStyles.languageChoices}>
                {LANGUAGES.map(({ code, name, flag }) => (
                  <ButtonBase
                    key={code}
                    lang={code}
                    title={name}
                    aria-label={name}
                    aria-pressed={i18n.resolvedLanguage === code}
                    sx={
                      i18n.resolvedLanguage === code
                        ? sxStyles.languageChoiceSelected
                        : sxStyles.languageChoice
                    }
                    onClick={() => onChangeLanguage(code)}
                  >
                    <Box component="img" src={flag} alt="" sx={sxStyles.flag} />
                    {code.toUpperCase()}
                  </ButtonBase>
                ))}
              </Box>
            </Box>
          </Box>
        </Popover>
      </>
    );
  }

  return result;
};

export default UserMenu;
